import { registerLazy } from '@norkron/web-conformance/register-lazy';
import { registerCodes } from '@norkron/web-errors';
import { useState, useRef, useCallback, useEffect } from 'react';

// src/index.ts

// src/self-check.ts
var LIB_ID = "@norkron/web-floating";
var OVERFLOW_TOLERANCE_PX = 1;
var SEVERITY_RANK = {
  info: 0,
  minor: 1,
  major: 2,
  critical: 3
};
function meetsFloor(finding, floor) {
  return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor];
}
function selectorFor(el) {
  const testid = el.getAttribute("data-testid");
  if (testid) return `[data-testid="${testid}"]`;
  const tag = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : "";
  return id ? `${tag}${id}` : tag;
}
function rectsOverlap(a, b) {
  if (a.width <= 0 || a.height <= 0 || b.width <= 0 || b.height <= 0) return false;
  return !(a.right <= b.left || b.right <= a.left || a.bottom <= b.top || b.bottom <= a.top);
}
function selfCheck(root, opts) {
  const safeOpts = {
    budgetMs: opts?.budgetMs ?? 50,
    minSeverity: opts?.minSeverity ?? "info",
    locale: opts?.locale ?? "en"
  };
  const findings = [];
  const ownerDoc = root.ownerDocument;
  try {
    const canMeasure = typeof window !== "undefined" && typeof window.getComputedStyle === "function";
    if (!canMeasure) {
      return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
    }
    const floats = Array.from(root.querySelectorAll("[data-floating]"));
    const viewportW = typeof window !== "undefined" ? window.innerWidth || ownerDoc?.documentElement.clientWidth || 0 : 0;
    const viewportH = typeof window !== "undefined" ? window.innerHeight || ownerDoc?.documentElement.clientHeight || 0 : 0;
    for (const float of floats) {
      const rect = float.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) continue;
      if (viewportW > 0 && viewportH > 0) {
        const overRight = rect.right - viewportW;
        const overBottom = rect.bottom - viewportH;
        const overLeft = -rect.left;
        const overTop = -rect.top;
        const maxOver = Math.max(overRight, overBottom, overLeft, overTop);
        if (maxOver > OVERFLOW_TOLERANCE_PX) {
          findings.push({
            libId: LIB_ID,
            code: "WEB_FLOATING_VIEWPORT_OVERFLOW",
            category: "layout",
            severity: "major",
            message: `Floating element overflows the viewport by ${Math.round(maxOver)}px. The shift/flip pass failed to clamp it on-screen.`,
            selector: selectorFor(float),
            suggestedFix: "Ensure `autoUpdate()` is wired and `computePosition()` is called after layout; pass an explicit `boundary` that matches the visible viewport.",
            extras: {
              rect: { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom },
              viewport: { width: viewportW, height: viewportH }
            }
          });
        }
      }
      const anchorId = float.getAttribute("data-floating-anchor");
      if (anchorId) {
        const anchor = ownerDoc?.getElementById(anchorId);
        if (anchor) {
          const aRect = anchor.getBoundingClientRect();
          if (rectsOverlap(rect, aRect)) {
            findings.push({
              libId: LIB_ID,
              code: "WEB_FLOATING_OVERLAPS_ANCHOR",
              category: "layout",
              severity: "major",
              message: "Floating element overlaps its declared anchor. Floating positioning must keep clear of the anchor element (use offset / shift).",
              selector: selectorFor(float),
              suggestedFix: "Add a non-zero `offset` middleware (default 4-8px) when calling `computePosition()`, or check that the anchor ref points at the correct element.",
              extras: { anchorId }
            });
          }
        }
      }
    }
  } catch (err) {
    findings.push({
      libId: LIB_ID,
      code: "WEB_FLOATING_SELFCHECK_INTERNAL_ERROR",
      category: "identity",
      severity: "info",
      message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`
    });
  }
  return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
}
var WEB_FLOATING_LIB_ID = "@norkron/web-floating";
try {
  registerCodes(WEB_FLOATING_LIB_ID, {
    WEB_FLOATING_VIEWPORT_OVERFLOW: {
      description: "selfCheck: a `[data-floating]` element extends beyond the viewport by more than the tolerance (default ~4px). The flip/shift positioning pass failed to clamp the floating element on-screen. (Conformance severity: major.)",
      severity: "warning",
      kind: "contract",
      docsAnchor: "https://norkron.dev/web-floating/WEB_FLOATING_VIEWPORT_OVERFLOW",
      exampleFix: "Ensure `autoUpdate()` is wired and `computePosition()` is called after layout; pass an explicit `boundary` that matches the visible viewport."
    },
    WEB_FLOATING_OVERLAPS_ANCHOR: {
      description: "selfCheck: a floating element overlaps its declared `[data-floating-anchor]`. Floating positioning must keep clear of the anchor element (use offset / shift middleware). (Conformance severity: major.)",
      severity: "warning",
      kind: "contract",
      docsAnchor: "https://norkron.dev/web-floating/WEB_FLOATING_OVERLAPS_ANCHOR",
      exampleFix: "Add a non-zero `offset` middleware (default 4-8px) when calling `computePosition()`, or check that the anchor ref points at the correct element."
    },
    WEB_FLOATING_SELFCHECK_INTERNAL_ERROR: {
      description: "selfCheck: an internal error was caught and surfaced as a non-throwing finding (selfCheck never throws). Indicates a defect in the selfCheck itself, not in consumer code. (Conformance severity: info.)",
      severity: "info",
      kind: "contract",
      docsAnchor: "https://norkron.dev/web-floating/WEB_FLOATING_SELFCHECK_INTERNAL_ERROR",
      exampleFix: "File an issue against @norkron/web-floating with the finding.message stack context."
    }
  });
} catch {
}

// src/compute-position.ts
var OPPOSITE_SIDE = {
  top: "bottom",
  bottom: "top",
  left: "right",
  right: "left"
};
function normalisePlacement(p) {
  if (typeof p === "string") {
    const dash = p.indexOf("-");
    if (dash === -1) return { side: p, align: "center" };
    const side = p.slice(0, dash);
    const align = p.slice(dash + 1);
    return { side, align };
  }
  return { side: p.side, align: p.align ?? "center" };
}
function rectFromReference(ref) {
  if (typeof ref.getBoundingClientRect === "function") {
    const r = ref.getBoundingClientRect();
    return { x: r.left, y: r.top, width: r.width, height: r.height };
  }
  const obj = ref;
  return { x: obj.x, y: obj.y, width: obj.width, height: obj.height };
}
function rectFromFloating(floating) {
  const r = floating.getBoundingClientRect();
  return { x: r.left, y: r.top, width: r.width, height: r.height };
}
function nearestClippingAncestor(el) {
  let node = el?.parentElement ?? null;
  while (node && node !== document.body && node !== document.documentElement) {
    const style = getComputedStyle(node);
    if (style.overflow !== "visible" || style.overflowX !== "visible" || style.overflowY !== "visible") {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}
function boundaryRect(reference, boundary) {
  if (boundary === "viewport") {
    const w = typeof window !== "undefined" ? window.innerWidth : 0;
    const h = typeof window !== "undefined" ? window.innerHeight : 0;
    return { x: 0, y: 0, width: w, height: h };
  }
  if (boundary === "clippingAncestors") {
    const refEl = typeof reference.getBoundingClientRect === "function" ? reference : null;
    const clipper = refEl ? nearestClippingAncestor(refEl) : null;
    if (clipper) {
      const r2 = clipper.getBoundingClientRect();
      return { x: r2.left, y: r2.top, width: r2.width, height: r2.height };
    }
    const w = typeof window !== "undefined" ? window.innerWidth : 0;
    const h = typeof window !== "undefined" ? window.innerHeight : 0;
    return { x: 0, y: 0, width: w, height: h };
  }
  const r = boundary.getBoundingClientRect();
  return { x: r.left, y: r.top, width: r.width, height: r.height };
}
function fitsOnSide(side, ref, floating, b, offset, pad) {
  switch (side) {
    case "top":
      return ref.y - floating.height - offset >= b.y + pad;
    case "bottom":
      return ref.y + ref.height + floating.height + offset <= b.y + b.height - pad;
    case "left":
      return ref.x - floating.width - offset >= b.x + pad;
    case "right":
      return ref.x + ref.width + floating.width + offset <= b.x + b.width - pad;
  }
}
function placeOnSide(side, align, ref, floating, offset) {
  let x = 0;
  let y = 0;
  if (side === "top" || side === "bottom") {
    y = side === "top" ? ref.y - floating.height - offset : ref.y + ref.height + offset;
    const start = ref.x;
    const center = ref.x + ref.width / 2 - floating.width / 2;
    const end = ref.x + ref.width - floating.width;
    x = align === "start" ? start : align === "end" ? end : center;
  } else {
    x = side === "left" ? ref.x - floating.width - offset : ref.x + ref.width + offset;
    const start = ref.y;
    const center = ref.y + ref.height / 2 - floating.height / 2;
    const end = ref.y + ref.height - floating.height;
    y = align === "start" ? start : align === "end" ? end : center;
  }
  return { x, y };
}
function shiftIntoBoundary(pos, floating, b, pad) {
  const minX = b.x + pad;
  const maxX = b.x + b.width - floating.width - pad;
  const minY = b.y + pad;
  const maxY = b.y + b.height - floating.height - pad;
  const clampedX = Math.max(minX, Math.min(pos.x, maxX));
  const clampedY = Math.max(minY, Math.min(pos.y, maxY));
  const shifted = clampedX !== pos.x || clampedY !== pos.y;
  return { x: clampedX, y: clampedY, shifted };
}
function computePosition(reference, floating, options) {
  const placement = normalisePlacement(options.placement);
  const offset = options.offset ?? 0;
  const pad = options.collisionPadding ?? 0;
  const boundary = options.collisionBoundary ?? "viewport";
  const refRect = rectFromReference(reference);
  const floatRect = rectFromFloating(floating);
  const bRect = boundaryRect(reference, boundary);
  const allowed = options.allowedSides && options.allowedSides.length > 0 ? options.allowedSides : null;
  let side = placement.side;
  const align = placement.align;
  let flipped = false;
  if (allowed && !allowed.includes(side)) {
    side = allowed.includes(OPPOSITE_SIDE[side]) ? OPPOSITE_SIDE[side] : allowed[0];
  }
  if (!fitsOnSide(side, refRect, floatRect, bRect, offset, pad)) {
    const opposite = OPPOSITE_SIDE[side];
    if ((!allowed || allowed.includes(opposite)) && fitsOnSide(opposite, refRect, floatRect, bRect, offset, pad)) {
      side = opposite;
      flipped = true;
    }
  }
  const raw = placeOnSide(side, align, refRect, floatRect, offset);
  const shifted = shiftIntoBoundary(raw, floatRect, bRect, pad);
  return {
    x: shifted.x,
    y: shifted.y,
    placement: { side, align },
    side,
    align,
    flipped,
    shifted: shifted.shifted
  };
}

// src/resolve-length.ts
var ABSOLUTE_PER_PX = {
  px: 1,
  // CSS absolute units are defined against a 96dpi reference pixel.
  in: 96,
  cm: 96 / 2.54,
  mm: 96 / 25.4,
  q: 96 / 101.6,
  pt: 96 / 72,
  pc: 16
};
function rootFontSizePx(fallback = 16) {
  if (typeof document === "undefined") return fallback;
  const el = document.documentElement;
  if (!el) return fallback;
  const parsed = Number.parseFloat(getComputedStyle(el).fontSize);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}
function resolveLengthPx(value, fallbackPx = 0, contextFontSizePx) {
  if (typeof value === "number") return Number.isFinite(value) ? value : fallbackPx;
  if (typeof value !== "string") return fallbackPx;
  const trimmed = value.trim().toLowerCase();
  if (trimmed === "") return fallbackPx;
  const match = /^(-?(?:\d+\.?\d*|\.\d+))([a-z%]*)$/.exec(trimmed);
  if (!match) return fallbackPx;
  const n = Number.parseFloat(match[1]);
  if (!Number.isFinite(n)) return fallbackPx;
  const unit = match[2];
  if (unit === "") return n;
  if (unit === "rem") return n * rootFontSizePx();
  if (unit === "em") return n * (contextFontSizePx ?? rootFontSizePx());
  const perPx = ABSOLUTE_PER_PX[unit];
  if (perPx !== void 0) return n * perPx;
  return fallbackPx;
}

// src/auto-update.ts
function getScrollParents(el) {
  const parents = [];
  let node = el?.parentElement ?? null;
  while (node && node !== document.body && node !== document.documentElement) {
    const style = getComputedStyle(node);
    if (style.overflow !== "visible" || style.overflowX !== "visible" || style.overflowY !== "visible") {
      parents.push(node);
    }
    node = node.parentElement;
  }
  return parents;
}
function autoUpdate(reference, floating, update) {
  let cleaned = false;
  const refEl = typeof reference.getBoundingClientRect === "function" && reference.nodeType !== void 0 ? reference : null;
  const onResize = () => {
    if (!cleaned) update();
  };
  const onScroll = () => {
    if (!cleaned) update();
  };
  if (typeof window !== "undefined") {
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, { capture: true, passive: true });
  }
  const scrollParents = refEl ? getScrollParents(refEl) : [];
  for (const p of scrollParents) {
    p.addEventListener("scroll", onScroll, { passive: true });
  }
  let ro = null;
  if (typeof ResizeObserver !== "undefined") {
    ro = new ResizeObserver(() => {
      if (!cleaned) update();
    });
    if (refEl && refEl instanceof Element) ro.observe(refEl);
    if (floating instanceof Element) ro.observe(floating);
  }
  update();
  return () => {
    if (cleaned) return;
    cleaned = true;
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, { capture: true });
    }
    for (const p of scrollParents) p.removeEventListener("scroll", onScroll);
    if (ro) ro.disconnect();
  };
}
function useFloating(options) {
  const [reference, setReference] = useState(null);
  const [floating, setFloating] = useState(null);
  const [pos, setPos] = useState(null);
  const optsRef = useRef(options);
  optsRef.current = options;
  const update = useCallback(() => {
    if (!reference || !floating) return;
    const result = computePosition(reference, floating, optsRef.current);
    setPos(result);
  }, [reference, floating]);
  useEffect(() => {
    if (!reference || !floating) return;
    const cleanup = autoUpdate(reference, floating, update);
    return cleanup;
  }, [reference, floating, update]);
  return {
    x: pos?.x ?? null,
    y: pos?.y ?? null,
    placement: pos?.placement,
    side: pos?.side,
    align: pos?.align,
    flipped: pos?.flipped,
    shifted: pos?.shifted,
    refs: {
      setReference,
      setFloating,
      reference,
      floating
    },
    update
  };
}

// src/index.ts
registerLazy(
  "@norkron/web-floating",
  () => selfCheck
);

export { WEB_FLOATING_LIB_ID, autoUpdate, computePosition, resolveLengthPx, selfCheck, useFloating };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map